import styled, { css, keyframes } from 'styled-components';
import { hexToRgbA } from 'utils/colors';

const trialHintPulse = (ringColor: string) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${hexToRgbA(ringColor, 0.55)};
  }
  70% {
    box-shadow: 0 0 0 6px ${hexToRgbA(ringColor, 0)};
  }
  100% {
    box-shadow: 0 0 0 0 ${hexToRgbA(ringColor, 0)};
  }
`;

export const StyledTooltipWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const StyledButton = styled.button<{
  width: string;
  contained?: boolean;
  muted?: boolean;
  isMinusIcon?: boolean;
  $highlighted?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 0;
  height: ${({ width }) => width};
  width: ${({ width }) => width};
  min-height: ${({ width }) => width};
  min-width: ${({ width }) => width};
  aspect-ratio: 1;
  border: none;
  border-radius: 50%;
  outline: none;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    polygon:last-of-type {
      fill: ${({ theme }) => theme.color.pickledBluewood};
    }
  }

  ${({ muted, contained, theme }) =>
    muted
      ? css`
          background: rgba(48, 70, 89, 0.1);
          box-shadow: none;
          transition: background 0.2s ease, transform 0.2s ease;

          &:hover:enabled {
            background: rgba(48, 70, 89, 0.16);
          }

          &:active:enabled {
            transform: scale(0.96);
            background: rgba(48, 70, 89, 0.16);
          }

          &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 2px ${theme.color.brightTurquoise};
          }
        `
      : css`
          background: ${contained
            ? theme.color.brightTurquoise
            : theme.color.casper};
          box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
          transition: transform 0.3s;

          &:hover:enabled {
            transform: scale(1.1);

            svg {
              polygon:last-of-type {
                fill: ${theme.color.white};
              }
            }
          }

          &:active:enabled {
            background: ${contained
              ? theme.color.darkGradient
              : theme.color.casper};
            opacity: 80%;
          }
        `}

  ${({ muted, isMinusIcon, theme }) =>
    !muted &&
    isMinusIcon &&
    css`
      &:hover:enabled svg g:last-of-type {
        fill: ${theme.color.white};
      }
    `}

  &:disabled {
    opacity: 20%;
  }

  ${({ $highlighted, theme }) =>
    $highlighted &&
    css`
      background: ${hexToRgbA(theme.color.brightTurquoise, 0.22)};
      animation: ${trialHintPulse(theme.color.brightTurquoise)} 1.6s ease-out
        infinite;

      svg polygon:last-of-type {
        fill: ${theme.color.brightTurquoise};
      }

      &:hover:enabled {
        background: ${hexToRgbA(theme.color.brightTurquoise, 0.3)};
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        box-shadow: 0 0 0 3px ${hexToRgbA(theme.color.brightTurquoise, 0.45)};
      }
    `}
`;
